import { useMemo, useState } from 'react';
import { useTable } from '@refinedev/react-table';
import { type ColumnDef } from '@tanstack/react-table';
import { useDelete } from '@refinedev/core';
import type { ApiKey } from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TableSearch } from '@/components/TableSearch';
import { PageHeader } from '@/components/PageHeader';
import { fmtDate } from '@/lib/format';
import { useSearchFilter } from '@/lib/use-search-filter';

// Built once and memoised with an empty dep list (relations/List idiom):
// `@refinedev/react-table` compares `columns` by identity, and a fresh array
// per render makes its filter-sync effect re-run and wipe the toolbar's `q`
// filter (list-columns-stability.test). The revoke button is not disabled
// while a revoke is in flight — the confirm dialog covers the table.
function buildApiKeyColumns({ onRevoke }: { onRevoke: (key: ApiKey) => void }): ColumnDef<ApiKey>[] {
  return [
    { id: 'name', header: 'Name', accessorKey: 'name' },
    {
      id: 'prefix',
      header: 'Prefix',
      accessorKey: 'prefix',
      cell: (c) => <code className="font-mono text-xs">{c.getValue() as string}…</code>,
    },
    {
      id: 'userId',
      header: 'Bound to',
      accessorKey: 'userId',
      cell: (c) => {
        const v = c.getValue() as string | null;
        if (v) return <span className="font-mono text-xs">{v}</span>;
        // HUB-089: a track-bound key is the track's deploy credential.
        const trackId = c.row.original.trackId;
        if (trackId) return <span className="font-mono text-xs">track:{trackId}</span>;
        return <Badge variant="outline">machine</Badge>;
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'revokedAt',
      cell: (c) =>
        c.getValue() ? (
          <Badge variant="muted" className="rounded-full">
            <span className="size-1.5 rounded-full bg-destructive" aria-hidden="true" />
            Revoked
          </Badge>
        ) : (
          <Badge variant="success" className="rounded-full">
            <span className="size-1.5 rounded-full bg-success" aria-hidden="true" />
            Active
          </Badge>
        ),
    },
    {
      id: 'createdAt',
      header: 'Created',
      accessorKey: 'createdAt',
      cell: (c) => fmtDate(c.getValue() as string),
    },
    {
      id: 'actions',
      header: '',
      cell: (c) => (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="ghost"
            disabled={!!c.row.original.revokedAt}
            onClick={() => onRevoke(c.row.original)}
          >
            Revoke
          </Button>
        </div>
      ),
    },
  ];
}

export function ApiKeysList() {
  const { mutate: deleteOne, mutation } = useDelete();
  const isPending = mutation.isPending;
  const [pending, setPending] = useState<ApiKey | null>(null);

  const columns = useMemo(() => buildApiKeyColumns({ onRevoke: setPending }), []);
  const {
    reactTable: table,
    refineCore: { setFilters, filters },
  } = useTable<ApiKey>({ columns });
  const [search, setSearch] = useSearchFilter(setFilters, filters);

  const confirm = () => {
    if (!pending) return;
    deleteOne(
      { resource: 'api-keys', id: pending.id },
      {
        onSettled: () => setPending(null),
      },
    );
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="API Keys"
        description="Hub-wide API keys. Plaintext is shown once on creation — store it immediately."
        actions={[{ label: 'Mint new key', to: '/admin/api-keys/create' }]}
      />
      <DataTable
        table={table}
        toolbar={<TableSearch value={search} onChange={setSearch} placeholder="Filter by name or prefix…" />}
      />
      <ConfirmDialog
        open={!!pending}
        title="Revoke API key?"
        description={pending ? `"${pending.name}" will be revoked immediately and cannot be restored.` : ''}
        confirmLabel="Revoke"
        destructive
        busy={isPending}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </div>
  );
}
